import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { formatPHP } from "@/lib/format";
import { Search, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const TYPES = [
  { key: "", label: "All" },
  { key: "software", label: "Software" },
  { key: "hardware", label: "Hardware" },
  { key: "service", label: "Service" },
  { key: "bundle", label: "Bundle" },
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [type, setType] = useState(searchParams.get("type") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const { add } = useCart();

  const load = () => {
    const params = {};
    if (type) params.product_type = type;
    if (category) params.category = category;
    if (search) params.search = search;
    if (sort) params.sort = sort;
    api.get("/products", { params }).then((r) => setProducts(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const sp = {};
    if (type) sp.type = type;
    if (category) sp.category = category;
    if (search) sp.q = search;
    if (sort && sort !== "newest") sp.sort = sort;
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line
  }, [type, category, sort]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div data-testid="shop-page">
      <section className="bg-white border-b border-msr-line">
        <div className="msr-container py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-3">MSR Shop</div>
            <h1 className="font-heading font-extrabold text-4xl lg:text-6xl tracking-tight" data-testid="shop-title">
              Reliable technology for<br />modern Filipino businesses.
            </h1>
          </div>
          <p className="lg:col-span-5 text-msr-slate leading-relaxed">
            Business desktops, laptops, networking, servers, CCTV, POS, software licenses, and ready-to-deploy bundles — supplied, configured, and supported by MSR.
          </p>
        </div>
      </section>

      <section className="bg-msr-surface border-b border-msr-line">
        <div className="msr-container py-10 grid lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3 space-y-6" data-testid="shop-sidebar">
            <form onSubmit={onSearch} className="flex items-center gap-2 border border-msr-line bg-white px-3">
              <Search size={16} className="text-msr-slate" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full py-2.5 text-sm focus:outline-none"
                data-testid="shop-search-input"
              />
            </form>

            <div>
              <div className="overline mb-3">Type</div>
              <div className="flex flex-wrap gap-1.5">
                {TYPES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setType(t.key)}
                    className={`text-xs uppercase tracking-wider font-semibold px-3 py-1.5 border transition-colors ${
                      type === t.key ? "bg-msr-blue text-white border-msr-blue" : "bg-white text-msr-ink border-msr-line hover:border-msr-blue"
                    }`}
                    data-testid={`shop-type-${t.key || "all"}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="overline mb-3">Category</div>
              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => setCategory("")}
                  className={`block w-full text-left text-sm py-2 px-2 ${category === "" ? "text-msr-blue font-bold" : "text-msr-slate hover:text-msr-ink"}`}
                  data-testid="shop-category-all"
                >
                  All categories
                </button>
                {categories.map((c) => (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => setCategory(c.slug)}
                    className={`block w-full text-left text-sm py-2 px-2 ${category === c.slug ? "text-msr-blue font-bold" : "text-msr-slate hover:text-msr-ink"}`}
                    data-testid={`shop-category-${c.slug}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="overline mb-3">Sort</div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-full border border-msr-line bg-white px-3 py-2 text-sm" data-testid="shop-sort">
                <option value="newest">Newest</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="name">Name</option>
              </select>
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="text-xs uppercase tracking-wider text-msr-slate mb-5" data-testid="shop-result-count">{products.length} products</div>
            {products.length === 0 ? (
              <div className="msr-card text-center py-16" data-testid="shop-empty">
                <p className="text-msr-slate">No products match your filters.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p) => (
                  <ProductCard key={p.id} p={p} onAdd={() => { add(p, 1); toast.success(`${p.name} added to cart`); }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductCard({ p, onAdd }) {
  return (
    <div className="msr-card flex flex-col" data-testid={`shop-product-${p.slug}`}>
      <Link to={`/shop/${p.slug}`} className="block">
        <div className="relative -mx-6 -mt-6 lg:-mx-8 lg:-mt-8 mb-5 h-44 overflow-hidden bg-msr-mist">
          <img src={resolveImageUrl(p.featured_image)} alt={p.name} className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
          {p.compare_at_price > p.price && (
            <span className="absolute top-3 right-3 bg-msr-green text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1">Save</span>
          )}
          <span className="absolute bottom-3 left-3 badge-soft-blue bg-white/95">{p.product_type}</span>
        </div>
        <h3 className="font-heading font-bold text-lg leading-snug text-msr-ink">{p.name}</h3>
        <p className="text-sm text-msr-slate mt-2 line-clamp-2">{p.short_description}</p>
      </Link>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-heading font-extrabold text-xl text-msr-blue">{formatPHP(p.price)}</span>
        {p.compare_at_price > p.price && <span className="text-xs text-msr-slate line-through">{formatPHP(p.compare_at_price)}</span>}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-msr-slate mt-1">SKU: {p.sku} · Stock: {p.stock_quantity}</div>
      <div className="flex gap-2 mt-5">
        <button onClick={onAdd} className="btn-msr-primary flex-1 py-2.5 text-xs" data-testid={`shop-add-to-cart-${p.slug}`}>
          <ShoppingCart size={14} /> Add to Cart
        </button>
        <Link to={`/shop/${p.slug}`} className="btn-msr-outline py-2.5 text-xs" data-testid={`shop-view-${p.slug}`}>Details</Link>
      </div>
    </div>
  );
}
